/**
 * Who Consul works for in this tab: the signed-in user on their current
 * team (`<user id>:<team id>`), or null when no one is signed in. Consul's
 * conversations, its socket and what the browser keeps for it belong to one
 * identity. A sign-out and a sign-in, or a team switch, are Inertia visits
 * with no page load, so when the identity changes the last one's stored keys
 * go (below) and everything held for it is let go of by its owner
 * (lib/consul/store disposes its store, lib/consul/panelState closes the
 * panel). Followed from every page's props (app.jsx, on each visit) and set
 * by the layout. Light on purpose: SidebarLayout and app.jsx import it, and
 * it imports nothing.
 */
let identity = null;
const listeners = new Set();

/** The open conversation's id for the tab's session (sessionStorage), per identity. */
export const ACTIVE_KEY = 'consul.active';
/** Whether the panel is open (localStorage), per identity. */
export const OPEN_KEY = 'consul.panel.open';
/** When the panel last closed in this tab, in ms (sessionStorage), per identity. */
export const CLOSED_KEY = 'consul.panel.closed';

/** The identity a page's props name: `<user id>:<current team id>`, or null without a signed-in user. */
export function identityOf(props) {
    const user = props?.auth?.user;
    if (user === null || typeof user !== 'object' || user.id === undefined || user.id === null || user.id === '') return null;
    const team = user.current_team_id ?? user.current_team?.id ?? '';
    return `${user.id}:${team}`;
}

/** `base` as kept for `who` (the identity now, by default); null when no one is signed in. */
export const keyFor = (base, who = identity) => (who === null ? null : `${base}:${who}`);

// Storage may throw (a private window, blocked site data): what it holds is then gone anyway.
function forget(who) {
    try {
        window.sessionStorage.removeItem(keyFor(ACTIVE_KEY, who));
        window.sessionStorage.removeItem(keyFor(CLOSED_KEY, who));
    } catch {
        // nothing kept
    }
    try {
        window.localStorage.removeItem(keyFor(OPEN_KEY, who));
    } catch {
        // nothing kept
    }
}

export const getConsulIdentity = () => identity;

/** The identity now (`identityOf`'s form; anything else is no one). A change removes the last identity's keys, then tells every listener `(next, previous)`. */
export function setConsulIdentity(next) {
    const value = typeof next === 'string' && next !== '' ? next : null;
    if (value === identity) return;
    const previous = identity;
    identity = value;
    if (previous !== null) forget(previous);
    [...listeners].forEach(listener => listener(value, previous));
}

export function subscribeIdentity(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Follows the identity through every Inertia visit: `router` is Inertia's, `page` the first page. Returns the listener's removal. */
export function followConsulIdentity(router, page) {
    setConsulIdentity(identityOf(page?.props));
    return router.on('navigate', event => setConsulIdentity(identityOf(event?.detail?.page?.props)));
}
